import { parseToBool } from './string';
import { log as logger } from './utils';

const isDebuggerActive = parseToBool(
  process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS_DEBUG
);
const isAnalyticsActive = parseToBool(
  process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS_ENABLED
);

const log = (...args) => logger('ga', ...args);

export const sendEvent = (options) => {
  const { action, category, label, value } = options;
  const staticOptions = {
    // Identifier for the Promobit 2.0
    version: 2,
    // Using a non-interaction event for all cases is wrong, but was asked by
    // the product team. It might be fixed in the future as all non-interaction
    // are documented
    non_interaction: true,
  };

  if (isDebuggerActive) {
    log('Event sent with the following payload:\n', {
      ...options,
      ...staticOptions,
    });
    return;
  }

  if (!isAnalyticsActive) {
    return;
  }

  // Docs: https://developers.google.com/analytics/devguides/collection/gtagjs/events
  const eventMeta = {
    ...staticOptions,
    event_category: category,
    value,
  };

  if (label) {
    eventMeta.event_label = label;
  }

  window?.gtag('event', action, eventMeta);
};

// in GA4 the `page_view` is sended automatically, so after migration we can
// remove it
export const sendPageView = (url) => {
  if (isDebuggerActive) {
    log('Pageview sent with the following payload:\n', { page_path: url });
    return;
  }

  if (!isAnalyticsActive) {
    return;
  }

  // Docs: https://developers.google.com/analytics/devguides/collection/gtagjs/pages
  window?.gtag('config', process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS_ID, {
    page_path: url,
    custom_map: { dimension4: 'version' },
  });
};

export function sendWebVitalsToGoogleAnalytics(options) {
  const { name, value, id } = options;
  const currValue = Math.round(name === 'CLS' ? value * 1000 : value);
  const payload = {
    event_category: 'Web Vitals',
    // Google Analytics metrics must be integers, so the value is rounded.
    // For CLS the value is first multiplied by 1000 for greater precision
    value: currValue,
    // The `id` value will be unique to the current page load. When sending
    // multiple values from the same page (e.g. for CLS), Google Analytics
    // can compute a total by grouping on this ID (note: requires
    // `eventLabel` to be a dimension in your report)
    event_label: id,
    // Use a non-interaction event to avoid affecting bounce rate
    non_interaction: true,
  };

  if (isDebuggerActive) {
    log(`Web vital metric ${name} sent with the following payload:\n`, payload);
    return;
  }

  if (!isAnalyticsActive) {
    return;
  }

  // The GA4 is implemented here with the feat of dual tagging with the same
  // GTAG script. And in this app context, is used the UA script to configure
  // (temporarily) the GA4.
  // Ref: https://support.google.com/analytics/answer/9310895#gtagjs-enable-basic&zippy=%2Cin-this-article
  window?.gtag('config', process.env.NEXT_PUBLIC_GOOGLE_GA4_ID);
  window?.gtag('event', name, payload);
}

export function sendUserIdToGoogleAnalytics(userId) {
  if (isDebuggerActive) {
    log('Send user id to Google Analytics:\n', {
      user_id: userId,
    });
    return;
  }

  window?.gtag('config', process.env.NEXT_PUBLIC_GOOGLE_GA4_ID, {
    user_id: userId,
  });
  window?.gtag('set', { user_id: userId });
}
